import React, { useCallback, useEffect, useState } from 'react';
import Search from '../../shared/components/ui/Search';
import Table from '../../shared/components/layout/Table';
import filter from '../../../modules/shared/assets/svg/filter.svg';
import { transformData } from '../../super-admin/utils/transformData';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  updateBulkInvoiceStatus,
  getSPBillsList,
  paySPBills
} from '../services/api/implementation/impl';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  buildReturnToFromLocation,
  navigateWithReturn,
  useSmartBack
} from '../../shared/navigation';
import { useForm } from 'react-hook-form';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';
import DateFormat from '../../super-admin/utils/DateFormat';
import toast from 'react-hot-toast';
import FormInput from '../../shared/components/ui/FormInput';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import NoDataFound from '../components/layout/NoDataFound';
import PaginationComponent from '../../shared/components/PaginationComponent';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import Header from '../../shared/components/layout/Header';
import Layout from '../components/layout/AppLayout';
// import DeleteConfirmationModal from '../components/ui/DeleteConfirmationModal';
import Loader from '../../shared/components/ui/Loader';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { formatISO } from 'date-fns';
import SecondaryTabbedComponent from '../../shared/components/layout/SecondaryTabbedComponent';
import PerformaInvoiceList from './PerformaInvoiceList';
import { useProjectsList } from '../../../contexts/ProjectsContext';

const Payables = () => {
  const userId = Cookies.get('userId');
  const { onBack } = useSmartBack({ fallback: '/builder/dashboard' });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    setValue
  } = useForm();
  const { id: projectId } = useParams();
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedAmount, setSelectedAmount] = useState(0);
  const [dueAmount, setDueAmount] = useState(0);
  const [selctedStage, setSelctedStage] = useState('');
  const { collapsed } = useSidebar();

  // Tabs state
  const [activeTab, setActiveTab] = useState('Service Provider Invoices');
  const tabsMeta = { tabs: ['Service Provider Invoices', 'Performa Invoice (Vendor)'] };

  // pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // items per page

  const {
    data: Payables,
    refetch: refetchPayables,
    isFetching: isFetchingPayables
  } = useQuery({
    queryKey: ['payables', page],
    queryFn: () => getSPBillsList(page, limit),
    keepPreviousData: true
  });

  useEffect(() => {
    refetchPayables();
  }, []);

  useEffect(() => {
    if (projects) {
      setValue('project', projects?.[0]?.id);
    }
  }, [projects]);

  const totalPages = Payables?.meta ? Math.ceil(Payables?.meta.total / limit) : 1;

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const InvoiceFormat = [
    'id',
    'name',
    'due_amount',
    'paid_amount',
    'amount',
    'gst_percentage',
    'approval',
    'payment_status'
  ];

  const PayableData = Payables?.data?.map((item) =>
    transformData(
      {
        ...item,
        id: item.id,
        name: item?.project?.name,
        due_amount: item?.due_amount,
        paid_amount: item?.paid_amount,
        amount: item?.total_amount,
        gst_percentage: item?.gst,
        approval: item.approved ? 'Approved' : 'Pending',
        payment_status: item.status === 'Unpaid' ? 'ShowPaidButton' : 'Paid'
      },
      InvoiceFormat
    )
  );

  // Add this state for multiple payments
  const [paymentAmount, setPaymentAmount] = useState('');
  const [isPaid, setIsPaid] = useState(false);
  const [StatusId, setStatusId] = useState();

  // Dummy register for FormInput when not using react-hook-form
  const dummyRegister = () => ({
    onChange: () => {},
    onBlur: () => {},
    ref: () => {},
    name: ''
  });

  const sanitizeToNumber = (val) => {
    if (val === null || val === undefined) return NaN;
    const s = String(val)
      .replace(/[^0-9.-]/g, '')
      .trim();
    const n = Number(s);
    return Number.isFinite(n) ? n : NaN;
  };

  const { mutate: handleUpdateStatus } = useMutation({
    mutationFn: () => {
      const num = sanitizeToNumber(paymentAmount);
      const due = sanitizeToNumber(dueAmount);

      if (paymentAmount === '' || paymentAmount === null || paymentAmount === undefined) {
        const err = new Error('VALIDATION_EMPTY');
        err.isValidation = true;
        toast.error('Please enter the payment amount.');
        return Promise.reject(err);
      }

      if (!Number.isFinite(num) || num < 0) {
        const err = new Error('VALIDATION_INVALID_AMOUNT');
        err.isValidation = true;
        toast.error('Please enter a valid amount (0 or greater).');
        return Promise.reject(err);
      }

      if (!Number.isFinite(due)) {
        const err = new Error('VALIDATION_INVALID_DUE');
        err.isValidation = true;
        toast.error('Unable to validate against due amount.');
        return Promise.reject(err);
      }

      if (num > due) {
        toast.error('Amount should be less than or equal to the pending invoice amount.');
        const err = new Error('VALIDATION_DUE_AMOUNT');
        err.isValidation = true;
        return Promise.reject(err);
      }

      const payload = {
        paidAmount: num
      };
      return paySPBills(StatusId, payload);
    },
    onSuccess: () => {
      toast.success('Payment successful');
      setIsPaid(false);
      setPaymentAmount('');
      refetchPayables();
    },
    onError: (err) => {
      if (
        err?.isValidation ||
        err?.message === 'VALIDATION_DUE_AMOUNT' ||
        err?.message === 'VALIDATION_INVALID_AMOUNT' ||
        err?.message === 'VALIDATION_INVALID_DUE' ||
        err?.message === 'VALIDATION_EMPTY'
      )
        return;
      toast.error(err?.response?.data?.message || err?.message || 'Error to update payment');
    }
  });

  const handleStatus = (id) => {
    setStatusId(id);
    const filteredData = PayableData.filter((item) => parseInt(item.id) === parseInt(id));
    const row = filteredData?.[0];
    setSelectedAmount(sanitizeToNumber(row?.amount));
    setDueAmount(sanitizeToNumber(row?.due_amount));
    setSelctedStage(row?.stage);
    setIsPaid(true);
  };

  const resetPaidModal = () => {
    setIsPaid(false);
    setSelctedStage('');
    setSelectedAmount(0);
    setPaymentAmount('');
    reset();
  };

  return (
    <>
      <Layout>
        <Loader isVisible={isFetchingPayables} />
        {/* Header */}
        <div
          className={`fixed top-0 z-10 transition-all duration-300 ease-in-out bg-white
    ${collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'}
  `}>
          <Header data={{ name: `Performa Invoices` }} backArrow={true} onBack={onBack} />

          <SecondaryTabbedComponent
            data={tabsMeta}
            activePrimary={activeTab}
            onChangePrimaryTab={setActiveTab}
            rounded={true}
          />
        </div>

        {activeTab === 'Service Provider Invoices' && (
          <>
            {isPaid && (
              <Modal isDirty={true} confirmOnClose={true} width={55} onClose={resetPaidModal}>
                <div>
                  <div className="flex justify-center flex-col mb-8">
                    <p>Pending invoice amount - {dueAmount}</p>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleUpdateStatus();
                      }}
                      className="flex flex-col gap-4 pt-6">
                      <FormInput
                        width={40}
                        label={'How much are you paying'}
                        isRequired
                        register_key={`payingAmount`}
                        type={'number'}
                        valuePre={paymentAmount}
                        onType={(val) => setPaymentAmount(val?.target ? val.target.value : val)}
                        register={dummyRegister}
                      />
                      <div className="w-full flex justify-end items-center space-x-4 mt-8">
                        <Button type="button" onClick={resetPaidModal} displayType="secondary">
                          Cancel
                        </Button>
                        <Button type="submit">Submit</Button>
                      </div>
                    </form>
                  </div>
                </div>
              </Modal>
            )}

            <div className="w-full flex flex-col  z-5 bg-gray-100">
              {PayableData && PayableData?.length > 0 ? (
                <>
                  {/* Grid */}

                  <GridWrapper width={100} childrenCustomStyle="m-3">
                    <div className="table-container mt-[10rem] h-[calc(100vh-12rem)] overflow-auto ">
                      <style>{`
            .table-container .table-body {
              position: static !important;
              overflow-y: visible !important;
              max-height: none !important;
              height: auto !important;
              top: auto !important;
              padding-bottom: 0 !important;
            }
            .table-container .table-container {
              height: auto !important;
            }
            .table-container > div {
              height: auto !important;
            }
          `}</style>
                      <Table
                        headers={[
                          'Project Name',
                          'Due Amount',
                          'Paid Amount',
                          'Amount',
                          'GST(%)',
                          'Approval',
                          'Payment Status'
                        ]}
                        data={PayableData}
                        identifier={'id'}
                        isViewEnable={false}
                        isEditEnable={false}
                        onBtn={(id) => handleStatus(id)}
                        onEdit={(id) => console.log(id)}
                        onRowClick={(id) =>
                          navigateWithReturn(navigate, `/builder/accounts/payables/${id}`, {
                            returnTo: buildReturnToFromLocation(location),
                            state: { from: 'payables' }
                          })
                        }
                        isDeleteEnable={false}
                        onDelete={() => {}}
                        isWrap={true}
                      />
                      <PaginationComponent
                        page={page}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        isFetching={isFetchingPayables}
                      />
                    </div>
                  </GridWrapper>
                </>
              ) : (
                <div className="pt-[3rem]">
                  <NoDataFound disabledBtn screenHeight={true} />
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'Performa Invoice (Vendor)' && (
          <div className="mt-6">
            <PerformaInvoiceList />
          </div>
        )}
      </Layout>
    </>
  );
};

export default Payables;
